import { useMemo, useState } from 'react';

import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useSubscription } from 'react-stomp-hooks';
import { generateUUID } from 'utilitys/helpers/general';
import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import useHashmapConverter from 'hooks/useHashmapConverter';
import useUserHook from 'hooks/useUserHook';

import { useConfigurationStore } from 'store/configurationsStore';
import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useProjectTreeStore } from 'store/projectContract.store';
import { useVersionStore } from 'store/version.store';

import { useQueryCountersData } from 'api/queries/battleCards.query';
import { cardsDataQueryKey } from 'api/queries/cardsData.query';
import { useQueryGetBoardInit } from 'api/queries/getBoardInit.query';
import { listCardsDataQueryKey } from 'api/queries/getListFilteredCards.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';

import { mapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/MainFilters.utils';
import { createSpecialFiltersPayload } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard.utils';
import {
    columnsV8,
    initialState,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoardColumns.utils';
import { BulkAssign } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/bulkAssign/bulkAssign';
import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';

type Mapper = {
    id?: number;
    name: string;
    label?: string;
};

export const BulkAssignWrapper = () => {
    const { componentKey } = useConfigurationStore().battlecardsRowsBoard;
    const { table } = useBaseTable({
        widgetId: undefined,
        localFilters: undefined,
        componentKey,
        initialState,
        columns: columnsV8,
        widgetViewMode: WidgetView.page,
    });

    const currentVersion = useVersionStore((state) => state.version);
    const globalFilterData = useGlobalFilterDataStore((state) => state.globalFilterData);
    const { project, contract } = useProjectTreeStore();
    const projectId = project?.id;
    const contractId = contract?.id;
    const { id, versionToCompareId } = useParams<ProgramStatusUrlParams>();
    const hashcode = [id, versionToCompareId].filter((v): v is string => Boolean(v)).join(',');
    const { versionList: versionFromHashMap } = useHashmapConverter(hashcode);
    const latestVersionId = versionFromHashMap[0]?.versionDbId;

    let filters = useMemo(() => {
        if (!projectId || !contractId || !latestVersionId) return { filtersArray: [], flags: {} };

        const baseFilters = getFiltersData({
            [FilterNames.PROJECT_CARD_BOARD]: [projectId],
            [FilterNames.CONTRACT_CARD_BOARD]: [contractId],
            [FilterNames.VERSION_CARD_BOARD]: [latestVersionId],
            [FilterNames.ACTIVITY_TYPE_BOARD]: globalFilterData?.cardActivity,
            [FilterNames.FRAGNET_SEARCH]: globalFilterData?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: globalFilterData?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: globalFilterData?.tags
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.WBS_SEARCH]: globalFilterData?.wbs,
            [FilterNames.ACTIVITY_CODES]: globalFilterData?.activityCodes
                ?.filter((item) => !item.isExclude)
                .map((i) => i.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: globalFilterData?.activityCodes
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.ASSIGNEE_CARD_BOARD]: globalFilterData?.assignee,
            [FilterNames.IMPORTANT_CARD_BOARD]: globalFilterData?.important,
            [FilterNames.URGENT_TYPE_CARD_BOARD]: globalFilterData?.urgent,
            [FilterNames.FRAGNET_MILESTONE_ID_COMPLETION_CARD_BOARD]: globalFilterData?.completionPlanSegment,
            [FilterNames.DOCLINK_CARD_BOARD]: globalFilterData?.hasAttachments,
            [FilterNames.LAG_CARD_BOARD]: globalFilterData?.lagLead,
            [FilterNames.TRACKED_CARD_BOARD]: globalFilterData?.trackedActivity,
            [FilterNames.FRAGNET_FORWARD_SEARCH]: globalFilterData?.activityForwardFragnet,
            [FilterNames.TYPE_CARD_BOARD]: globalFilterData?.cardType,
            [FilterNames.CLUSTER_CARD_BOARD]: globalFilterData?.cluster,
            [FilterNames.DURATION_CARD_BOARD]: globalFilterData?.duration,
            [FilterNames.TOTAL_FLOAT_CARD_BOARD]: globalFilterData?.float,
            [FilterNames.ACTIVITY_NAME_CARD_BOARD]: globalFilterData?.relatedActivity,
            [FilterNames.RESOURCE_NAME_CARD_BOARD]: globalFilterData?.resourceName,
            [FilterNames.RESOURCE_TYPE_CARD_BOARD]: globalFilterData?.resourceType,
            [FilterNames.SUBMITTED_COMPLETION_CARD_BOARD]: globalFilterData?.submittedCompletion,
            [FilterNames.UPDATED_USER_CARD_BOARD]: globalFilterData?.updatedBy,
            [FilterNames.WBS_LEVEL_CARD_BOARD]: globalFilterData?.wbsLevel,
            [FilterNames.STATUS_CARD_BOARD]: globalFilterData?.status,
        });

        const specialFiltersPayload = createSpecialFiltersPayload(globalFilterData);
        const specialFilters = specialFiltersPayload?.filters ?? [];
        const flags = specialFiltersPayload?.flags ?? {};

        return { filtersArray: [...baseFilters, ...specialFilters], flags };
    }, [contractId, globalFilterData, latestVersionId, projectId]);

    const filtersPayload = {
        filter: {
            filters: filters.filtersArray,
            flags: filters.flags,
        },
        paging: { ...table.pagination, page: table.pagination.page - 1 },
        withDetails: true,
        // sortingRequestList: table.sorting,
    };

    const pillsData = {
        cardActivity: globalFilterData?.cardActivity,
        assignee: globalFilterData?.assignee,
        important: globalFilterData?.important,
        urgent: globalFilterData?.urgent,
        completionPlanSegment: globalFilterData?.completionPlanSegment,
        hasAttachments: globalFilterData?.hasAttachments,
        lagLead: globalFilterData?.lagLead,
        trackedActivity: globalFilterData?.trackedActivity,
        activityForwardFragnet: globalFilterData?.activityForwardFragnet,
        cardType: globalFilterData?.cardType,
        cluster: globalFilterData?.cluster,
        duration: globalFilterData?.duration,
        float: globalFilterData?.float,
        relatedActivity: globalFilterData?.relatedActivity,
        resourceName: globalFilterData?.resourceName,
        submittedCompletion: globalFilterData?.submittedCompletion,
        updatedBy: globalFilterData?.updatedBy,
        wbsLevel: globalFilterData?.wbsLevel,
        status: globalFilterData?.status,
        resourceType: globalFilterData?.resourceType,
        milestoneFragnet: globalFilterData?.milestoneFragnet,
        wbs: globalFilterData?.wbs,
        finishDate: globalFilterData?.finishDate,
        startDate: globalFilterData?.startDate,
        baselineFinishDate: globalFilterData?.baselineFinishDate,
        baselineStartDate: globalFilterData?.baselineStartDate,
        hasComments: globalFilterData?.hasComments,
        riskIndicators: globalFilterData?.riskIndicators,
        searchByName: globalFilterData?.searchByName,
        activityCodes: globalFilterData?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: globalFilterData?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        tags: globalFilterData?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: globalFilterData?.tags?.filter((item) => item.isExclude).map((i) => i.name),
    };

    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId });

    const { email } = useUserHook();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();

    // state ===================================================================================================
    const [bulkAssigneeToUserUniqIdNotification, setBulkAssigneeToUserUniqIdNotification] =
        useState<number>(generateUUID());
    const [bulkAssigneeToTagsUniqIdNotification, setBulkAssigneeToTagsUniqIdNotification] =
        useState<number>(generateUUID());
    const [bulkRemoveTagsUniqIdNotification, setBulkRemoveTagsUniqIdNotification] = useState<number>(generateUUID());
    const [bulkDeleteCardsUniqIdNotification, setBulkDeleteCardsUniqIdNotification] = useState<number>(generateUUID());
    const [bulkRollbackUniqIdNotification, setBulkRollbackUniqIdNotification] = useState<number>(generateUUID());

    const [isBulkLoading, setIsBulkLoading] = useState<boolean>(false);

    useSubscription(email ? [`/topic/${email}.notifications.push`] : [], (response) => {
        const { type, message } = JSON.parse(response.body);
        if (type === 'BulkAssignUserStatus' && Number(message) === bulkAssigneeToUserUniqIdNotification) {
            refecthBySubscription();
            handleSnackBar('Bulk request submitted', 'success');
            setBulkAssigneeToUserUniqIdNotification(generateUUID());
            setIsBulkLoading(false);
        }
        if (type === 'BulkAssignTagsStatus' && Number(message) === bulkAssigneeToTagsUniqIdNotification) {
            refecthBySubscription();
            handleSnackBar('Bulk request submitted', 'success');
            setBulkAssigneeToTagsUniqIdNotification(generateUUID());
            setIsBulkLoading(false);
        }
        if (type === 'BulkRemoveTagsStatus' && Number(message) === bulkRemoveTagsUniqIdNotification) {
            refecthBySubscription();
            handleSnackBar('Bulk request submitted', 'success');
            setBulkRemoveTagsUniqIdNotification(generateUUID());
            setIsBulkLoading(false);
        }
        if (type === 'BulkDeleteCardStatus' && Number(message) === bulkDeleteCardsUniqIdNotification) {
            refecthBySubscription();
            handleSnackBar('Bulk request submitted', 'success');
            setBulkDeleteCardsUniqIdNotification(generateUUID());
            setIsBulkLoading(false);
        }
        if (type === 'BulkRollbackInterimStatus' && Number(message) === bulkRollbackUniqIdNotification) {
            refecthBySubscription();
            handleSnackBar('Bulk request submitted', 'success');
            setBulkRollbackUniqIdNotification(generateUUID());
            setIsBulkLoading(false);
        }
    });

    const { data: boardInit } = useQueryGetBoardInit({
        metaDataId: currentVersion?.id,
    });

    const { data: cardsCounters, refetch: cardsCountersRefetch } = useQueryCountersData(filtersPayload);

    const refecthBySubscription = () => {
        if (filtersPayload?.filter.filters.length > 0) {
            cardsCountersRefetch();
            return Promise.all([
                queryClient.invalidateQueries([cardsDataQueryKey]),
                queryClient.invalidateQueries([listCardsDataQueryKey]),
            ]);
        }
    };

    const mapper = (i: Mapper) => ({
        id: i.id,
        value: i.name,
        label: i.label || i.name,
    });

    const tagsOptions = boardInit ? boardInit.tags.map(mapper) : [];

    return (
        <>
            {isBulkLoading && <OverlayWithSpinner />}
            <BulkAssign
                pills={pills}
                tagsOptions={tagsOptions}
                searchedFilterData={filtersPayload}
                bulkAssigneeToUserUniqIdNotification={bulkAssigneeToUserUniqIdNotification}
                bulkAssigneeToTagsUniqIdNotification={bulkAssigneeToTagsUniqIdNotification}
                bulkRemoveTagsUniqIdNotification={bulkRemoveTagsUniqIdNotification}
                bulkDeleteCardsUniqIdNotification={bulkDeleteCardsUniqIdNotification}
                bulkRollbackUniqIdNotification={bulkRollbackUniqIdNotification}
                setIsBulkLoading={setIsBulkLoading}
                cardsCounters={cardsCounters}
                versionDbId={currentVersion?.id}
            />
        </>
    );
};
